import {
  createLineDropDisplayLine,
  type ColoredSegment,
  type DisplayLine,
} from './Cbullshitcoding.typing';

// Inline colour markup: ESC[<color>m ... ESC[0m, matching the dossier renderers.
// ESC (char 27) is used because it never appears in real content.
const COLOR_ESC = String.fromCharCode(27);
const COLOR_SEQUENCE = new RegExp(`${COLOR_ESC}\\[([^m]*)m`, 'g');
const COLOR_RESET_TOKENS = new Set(['', '0']);

/** Split a raw line into plain text + colour segments by parsing ESC[<color>m markup. */
function parseColoredLine(raw: string): DisplayLine {
  const segments: ColoredSegment[] = [];
  let lastIndex = 0;
  let currentColor: string | null = null;
  COLOR_SEQUENCE.lastIndex = 0;
  let match: RegExpExecArray | null = COLOR_SEQUENCE.exec(raw);
  while (match != null) {
    if (match.index > lastIndex) {
      segments.push({ text: raw.slice(lastIndex, match.index), color: currentColor });
    }
    currentColor = COLOR_RESET_TOKENS.has(match[1]) ? null : match[1];
    lastIndex = COLOR_SEQUENCE.lastIndex;
    match = COLOR_SEQUENCE.exec(raw);
  }
  if (lastIndex < raw.length) {
    segments.push({ text: raw.slice(lastIndex), color: currentColor });
  }
  const plain = segments.map((segment) => segment.text).join('');
  return {
    plain,
    segments: segments.length > 0 ? segments : [{ text: '', color: null }],
  };
}

/** Keep the first `maxVisible` characters across segments with colours preserved. */
export function sliceSegments(input: {
  segments: ColoredSegment[];
  maxVisible: number;
}): ColoredSegment[] {
  const result: ColoredSegment[] = [];
  let remaining = input.maxVisible;
  for (const segment of input.segments) {
    if (remaining <= 0) {
      break;
    }
    const text = segment.text.slice(0, remaining);
    result.push({ text, color: segment.color });
    remaining -= text.length;
  }
  return result.length > 0 ? result : [{ text: '', color: null }];
}

/** Drop the first `count` characters across segments for common-indent trimming. */
function dropLeadingSegments(input: {
  segments: ColoredSegment[];
  count: number;
}): ColoredSegment[] {
  const result: ColoredSegment[] = [];
  let toDrop = input.count;
  for (const segment of input.segments) {
    if (toDrop <= 0) {
      result.push(segment);
      continue;
    }
    if (toDrop >= segment.text.length) {
      toDrop -= segment.text.length;
      continue;
    }
    result.push({ text: segment.text.slice(toDrop), color: segment.color });
    toDrop = 0;
  }
  return result.length > 0 ? result : [{ text: '', color: null }];
}

/** Produce inline colour markup the parser understands. */
export function colorizeMarkup(text: string, color: string): string {
  return text
    .split(/(\s+)/)
    .map((part) =>
      part.length === 0 || /^\s+$/.test(part)
        ? part
        : `${COLOR_ESC}[${color}m${part}${COLOR_ESC}[0m`,
    )
    .join('');
}

/** Replace {{KEY}} placeholders in a line with their possibly colour-marked values. */
function applyReplacements(input: {
  line: string;
  replacements: Record<string, string>;
}): string {
  return Object.entries(input.replacements).reduce(
    (result, [key, value]) => result.split(`{{${key}}}`).join(value),
    input.line,
  );
}

function appendTrailingLineDrops(input: {
  lines: DisplayLine[];
  count: number;
}): DisplayLine[] {
  if (input.count <= 0 || input.lines.length === 0) {
    return input.lines;
  }

  return [
    ...input.lines,
    ...Array.from({ length: input.count }, createLineDropDisplayLine),
  ];
}

function getLeadingWhitespaceLength(value: string): number {
  return value.match(/^\s*/)?.[0].length ?? 0;
}

function normalizeDisplayLines(input: {
  strings: string[];
  maxLineLength?: number;
  trimCommonIndent: boolean;
}): DisplayLine[] {
  const rightTrimmed = input.strings.map((line) => {
    const parsed = parseColoredLine(line);
    const plain = parsed.plain.replace(/\s+$/, '');
    return {
      plain,
      segments: sliceSegments({ segments: parsed.segments, maxVisible: plain.length }),
    };
  });
  const nonEmptyLines = rightTrimmed.filter((line) => line.plain.trim().length > 0);
  const commonIndent =
    input.trimCommonIndent && nonEmptyLines.length > 0
      ? Math.min(...nonEmptyLines.map((line) => getLeadingWhitespaceLength(line.plain)))
      : 0;

  return rightTrimmed.map((line) => {
    let plain = line.plain;
    let segments = line.segments;
    if (commonIndent > 0 && plain.trim().length > 0) {
      plain = plain.slice(commonIndent);
      segments = dropLeadingSegments({ segments, count: commonIndent });
    }
    if (input.maxLineLength != null && plain.length > input.maxLineLength) {
      plain = plain.slice(0, input.maxLineLength);
      segments = sliceSegments({ segments, maxVisible: input.maxLineLength });
    }
    return { plain, segments };
  });
}

export function getDisplayLines(input: {
  loadedStrings: string[];
  propStrings?: string[];
  replacements?: Record<string, string>;
  maxDisplayLineLength?: number;
  trailingLineDropCount: number;
  trimCommonIndent: boolean;
}): DisplayLine[] {
  const activeStrings = input.loadedStrings.length > 0
    ? input.loadedStrings
    : input.propStrings ?? [];
  const substituted = input.replacements
    ? activeStrings.map((line) =>
      applyReplacements({ line, replacements: input.replacements ?? {} }),
    )
    : activeStrings;
  const displayLines = normalizeDisplayLines({
    strings: substituted,
    maxLineLength: input.maxDisplayLineLength,
    trimCommonIndent: input.trimCommonIndent,
  });

  return appendTrailingLineDrops({
    lines: displayLines,
    count: input.trailingLineDropCount,
  });
}
